import React, { useState, useEffect } from 'react';
import { Brain, RefreshCw, AlertCircle, Lightbulb, ExternalLink, Sparkles, MapPin, Clock } from 'lucide-react';
import { CommanServices } from '@/services/commanServices';

/* Light premium AI Insights card (spec: claude-code-incident-trend-spec.md).
   Real insight feed from /analytics/ai-insights; amber insight cards. */

const CARD_STYLE = {
  background: 'linear-gradient(180deg,#fff,#fcfcff)',
  border: '1px solid #e6e9f4',
  borderRadius: 20,
  boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 14px 40px rgba(23,32,84,0.08)',
  padding: '22px 24px',
  fontFamily: "'IBM Plex Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const DashboardAIInsightsWidget = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await CommanServices.getanalyticsaiinsights();
      if (response.status) {
        const data = await response.data;
        setInsights(data);
        setLastUpdated(new Date(data.generated_at));
      }
    } catch (err) {
      setError('Failed to load AI insights');
      console.error('AI insights error:', err);
    } finally {
      setLoading(false);
    }
  };

  const header = (
    <div className="flex items-center gap-3 mb-4">
      <span
        className="flex h-10 w-10 items-center justify-center rounded-xl text-white"
        style={{ background: 'linear-gradient(135deg,#7c3aed,#a855f7)', boxShadow: '0 6px 18px rgba(124,58,237,0.3)' }}
      >
        <Brain className="h-5 w-5" />
      </span>
      <h3 className="text-[17px] font-bold" style={{ color: '#0f172a' }}>AI Insights</h3>
      <span
        className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11.5px] font-bold"
        style={{ background: 'linear-gradient(180deg,#f5f3ff,#faf5ff)', border: '1px solid #ddd6fe', color: '#6d28d9' }}
      >
        <Sparkles className="h-3 w-3" /> Recommended
      </span>
      <button
        onClick={fetchInsights}
        title="Refresh insights"
        className="ml-auto flex h-[34px] w-[34px] items-center justify-center rounded-full transition-colors hover:text-[#0f172a]"
        style={{ background: '#f5f6fb', border: '1px solid #e9ecf6', color: '#64748b' }}
        data-testid="ai-insights-refresh"
      >
        <RefreshCw className="h-4 w-4" />
      </button>
    </div>
  );

  const footer = (
    <div className="mt-5 flex items-center justify-between pt-3 text-[12px]" style={{ borderTop: '1px solid #edf0f9' }}>
      <span className="flex items-center gap-1.5" style={{ color: '#94a3b8' }}>
        <Clock className="h-3.5 w-3.5" />
        Last updated: {lastUpdated ? lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}
      </span>
      <span className="flex items-center gap-1 font-semibold" style={{ color: '#a78bfa' }}>
        <Sparkles className="h-3.5 w-3.5" /> Powered by AI Pattern Detection
      </span>
    </div>
  );

  if (loading) {
    return (
      <div style={CARD_STYLE} data-testid="ai-insights-widget">
        {header}
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 bg-gray-100 rounded animate-pulse" />
              <div className="h-3 bg-gray-100 rounded w-3/4 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={CARD_STYLE} data-testid="ai-insights-widget">
        {header}
        <div className="text-center py-6">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-2">{error}</p>
          <button onClick={fetchInsights} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50">
            <RefreshCw className="h-3 w-3" /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={CARD_STYLE} data-testid="ai-insights-widget">
      {header}
      {!insights || !insights.insights || insights.insights.length === 0 ? (
        <div className="text-center py-10" style={{ color: '#94a3b8' }}>
          <Lightbulb className="h-14 w-14 mx-auto mb-3 opacity-40" />
          <p className="text-base font-semibold" style={{ color: '#64748b' }}>No recommendations at this time</p>
          <p className="text-sm">AI analysis will appear here when patterns are detected</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {insights.insights.map((insight) => (
            <div
              key={insight.id}
              className="flex items-start gap-3"
              style={{
                background: 'linear-gradient(160deg,#fffbf5,#fff8ed)',
                border: '1px solid #fde8c8',
                borderInlineStart: '3px solid #f59e0b',
                borderRadius: 14,
                padding: '17px 19px',
              }}
              data-testid={`ai-insight-${insight.id}`}
            >
              <span className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-[10px]" style={{ background: '#fef3c7', color: '#d97706' }}>
                <MapPin className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <h4 className="text-[15px] font-bold leading-snug" style={{ color: '#0f172a' }}>{insight.title}</h4>
                <p className="mt-1 text-[13.5px]" style={{ color: '#64748b', lineHeight: 1.6 }}>{insight.explanation}</p>
                <button
                  onClick={() => { window.location.href = insight.action.href; }}
                  className="mt-2.5 inline-flex items-center gap-1.5 rounded-[10px] px-3 py-1.5 text-[13px] font-bold transition-colors"
                  style={{ background: '#fff', border: '1px solid #fcd9a0', color: '#b45309', boxShadow: '0 2px 8px rgba(217,119,6,0.1)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#fffbeb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                >
                  {insight.action.label}
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {footer}
    </div>
  );
};

export default DashboardAIInsightsWidget;
